/**
 * Platformer particle system — a small, dependency-free CPU emitter
 * that renders into a Pixi container. Shared by the in-game
 * `ParticleEmitterEntity` and the editor's live preview, so a designer
 * sees exactly what ships.
 *
 * The stack is split along its seams (file-size rule):
 *
 *   ParticleConfig.js   constants, quality caps, props → config
 *   ParticleShapes.js   geometry tessellation (the look)
 *   ParticlePhysics.js  collision / player interaction / pile logic
 *   ParticleSystem.js   ← you are here: the pooled emitter class.
 *                         Re-exports the whole public API, so import
 *                         everything from this path as before.
 *
 * Performance model (kept genuinely lightweight, confetti.js-class — built
 * to hold 60fps on low-end hardware like a Raspberry Pi or a budget phone):
 *   - Particles are POOLED — nodes (Graphics) are reused, never churned. A
 *     free-stack makes spawn O(1) and the update walks a PACKED active list,
 *     so an emitter with a big historical peak but few live particles (or a
 *     deep, mostly-static pile) costs almost nothing — work is O(live), not
 *     O(poolSize).
 *   - Resting (piled / accumulated) flakes are frozen at landing and skip
 *     ALL per-frame transform writes (only a death-fade alpha), so deep snow
 *     buildup stays cheap.
 *   - Each node's geometry is tessellated ONCE (white, at a fixed BASE
 *     size) when it spawns. Per frame we only touch cheap transform +
 *     tint state (`x/y`, `scale`, `rotation`, `tint`, `alpha`) — we
 *     never call `clear()` / re-draw, so there is no per-frame
 *     tessellation. Colour is the white geometry recoloured via `tint`.
 *   - `maxParticles` is a SAFETY ceiling on the pool, not a throttle: the
 *     working cap is auto-raised to at least `rate × lifetime` (the count a
 *     continuous stream keeps alive) so a long lifetime never stalls the
 *     stream into a once-per-lifetime pulse. Per-frame cost stays
 *     O(activeParticles) of plain number math + transform writes; the pool
 *     only allocates nodes as they're actually needed.
 *
 * Emission model (Godot CPUParticles2D-flavoured, compact): see
 * `emitterConfigFromProps` in ParticleConfig.js for the full knob list —
 * rate/lifetime/speed/angle/spread/gravity, animated forces (radial,
 * tangential, damping, spin, turbulence), looks (colors, palette, sizes,
 * alphas, shapes, image mode, blend), collision ('ground'/'ground+player',
 * bounce, accumulate piles), and player interaction (wake/push/attract/
 * repel/swirl).
 *
 * Emission control (lets the host drive "action" triggers):
 *   - setEmitting(on) gate the continuous `rate` stream. Runtime triggers
 *                     (proximity / contact) toggle this; the editor
 *                     preview leaves it ON so the look is always visible.
 *   - burst(n)        spawn n particles in one shot (contact/jump bursts).
 *
 * The host supplies the collider via `setColliders({ cellSize, solidAt,
 * oneWayAt })` and, for player hits, `setBodyProvider(() => [aabb,…])`.
 * The editor preview passes the board's tile grid (no player), the
 * runtime entity passes the board + a live player getter.
 *
 * Positions are world-space; the host just parents the container under
 * whatever transform (world camera / editor pan-zoom) it lives in.
 */

import { Graphics, Sprite, Texture } from '../../../vendor/pixi.min.mjs';
import {
    num, clamp, lerp, lerpColor, BASE, ACCUM_SETTLE, DEG,
    PARTICLE_QUALITY_CAPS, setParticleQuality, setParticleHardCap,
    getParticleHardCap, PLAYER_INTERACT_MODES, emitterConfigFromProps,
} from './ParticleConfig.js';
import { buildShape } from './ParticleShapes.js';
import {
    applyPlayerInteract, integrateCollide, onRestingDeath,
} from './ParticlePhysics.js';

// Barrel re-export: the public API stays importable from this path.
export {
    PARTICLE_QUALITY_CAPS, setParticleQuality, setParticleHardCap,
    getParticleHardCap, PLAYER_INTERACT_MODES, emitterConfigFromProps,
};

export class ParticleSystem {
    /**
     * @param {import('../../../vendor/pixi.min.mjs').Container} container
     *        Pixi container the particle nodes render into.
     * @param {object} config  normalised config (see emitterConfigFromProps)
     */
    constructor(container, config) {
        this.container = container;
        this.cfg = config;
        // Pooled records: { node, x, y, vx, vy, age, life, rot, spinRate, shape, active }.
        // `_particles` owns every allocated record (never shrinks). Two
        // derived views keep the hot path O(active), not O(pool):
        //   _live — packed array of the currently-active records; the update
        //           loop walks ONLY these (no scanning dead slots) and uses
        //           swap-remove on death, so a near-empty emitter with a big
        //           historical peak costs nothing.
        //   _free — stack of inactive records ready to reuse, so _acquire()
        //           is O(1) instead of a linear scan for a free slot.
        this._particles = [];
        this._live = [];
        this._free = [];
        this._accum = 0;        // fractional particle accumulator for rate
        this._originX = 0;
        this._originY = 0;
        // Optional collision context (null = no collision, the default).
        // `_colliders` is the static tile grid; `_bodyProvider` returns
        // the live dynamic AABBs (the player) to bounce particles off.
        this._colliders = null;
        this._bodyProvider = null;
        // Image-mode texture (null until the host loads + injects it) and
        // its reference dimension so `size` reads as on-screen pixels.
        this._texture = null;
        this._texRefDim = BASE;
        // Continuous-emission gate. Runtime triggers toggle it; the
        // editor preview leaves it on so the effect is always visible.
        this._emitting = true;
        // Phase clock for the turbulent-displace flow field (advances by
        // dt·turbSpeed each frame so the wind churns over time).
        this._turbTime = 0;
        // Accumulation (snow buildup). Landed flakes PARK as real resting
        // particles that stack per column and live out their lifetime — no
        // flat fill. `_stacks` is a lazily built
        // Map<columnBucket, {floorY, surfaceY, items:[p,…]}>; `items` runs
        // bottom→top (oldest→newest) so deaths + settling stay cheap.
        this._stacks = null;
        this._accumBucket = 8;     // column width (px); refined from size/cell
    }

    setOrigin(x, y) { this._originX = x; this._originY = y; }

    setConfig(config) { this.cfg = config; }

    /**
     * Supply the static collision grid particles sweep against when an
     * emitter opts into collision.
     * @param {{cellSize:number,
     *          solidAt:(c:number,r:number)=>boolean,
     *          oneWayAt?:(c:number,r:number)=>boolean} | null} colliders
     */
    setColliders(colliders) {
        this._colliders = (colliders && typeof colliders.solidAt === 'function') ? colliders : null;
        // Stack columns ~one flake wide so the pile reads as an organic
        // heap of real particles (adjacent columns fill to different
        // heights → a natural mound), not chunky bars. Clamped to the cell.
        if (this._colliders) {
            const cs = this._colliders.cellSize || 32;
            const sz = num(this.cfg.startSize, 8);
            this._accumBucket = Math.max(3, Math.min(Math.round(sz), cs));
        }
    }

    /**
     * Supply a getter for dynamic bodies (e.g. the player) to bounce
     * particles off in 'ground+player' mode and to drive Player interaction
     * (wake/push/attract/repel/swirl). Returns an array of
     * `{ x, y, w, h, vx?, vy? }` AABBs (velocity in px/s, used by the
     * wake/push modes), re-read once per frame.
     * @param {(() => Array<{x:number,y:number,w:number,h:number,vx?:number,vy?:number}>) | null} fn
     */
    setBodyProvider(fn) {
        this._bodyProvider = typeof fn === 'function' ? fn : null;
    }

    /**
     * Supply the PNG texture used by an image-mode emitter (shape →
     * 'image'). Loaded asynchronously by the host from a skin slot;
     * until it arrives, image particles render the EMPTY texture (i.e.
     * invisible). Updates every pooled sprite in place. `_texRefDim` is
     * the texture's longest side so the `size` lerp reads as on-screen
     * pixels regardless of the source PNG's resolution.
     */
    setTexture(tex) {
        if (!tex) return;
        this._texture = tex;
        const w = tex.width || tex.frame?.width || BASE;
        const h = tex.height || tex.frame?.height || BASE;
        this._texRefDim = Math.max(1, Math.max(w, h));
        if (!this._isImage()) return;
        for (const p of this._particles) {
            if (p.node) p.node.texture = tex;
        }
    }

    /**
     * Gate the continuous `rate` stream. Runtime emitters with a
     * proximity trigger toggle this each frame; the editor preview never
     * calls it, so previews always emit and stay tunable.
     */
    setEmitting(on) { this._emitting = !!on; }

    /**
     * Emit `n` particles immediately (one-shot) — used by contact / jump
     * triggers for a burst. Respects the pool cap.
     */
    burst(n) {
        const count = Math.max(0, Math.min(this.cfg.maxParticles, n | 0));
        for (let i = 0; i < count; i++) this._spawn();
    }

    /** True when this emitter renders textured-PNG sprites. Confetti
     *  always wins as flat paper, so image mode is normal-engine only. */
    _isImage() {
        return !!this.cfg.image && this.cfg.engine !== 'confetti';
    }

    /** Active particle count — handy for the editor HUD / debugging. O(1). */
    get activeCount() { return this._live.length; }

    _acquire() {
        // Reuse a retired record (O(1)) before allocating a new node.
        const recycled = this._free.pop();
        if (recycled) return recycled;
        if (this._particles.length >= this.cfg.maxParticles) return null;
        // Image-mode emitters render textured Sprites; everything else a
        // tinted Graphics primitive. The choice is fixed for the system's
        // life (a config change rebuilds the whole emitter), so a pool
        // node never has to switch class mid-flight.
        const node = this._isImage()
            ? new Sprite(this._texture || Texture.EMPTY)
            : new Graphics();
        node.anchor?.set?.(0.5);
        const p = { node, x: 0, y: 0, vx: 0, vy: 0, age: 0, life: 1, rot: 0, spinRate: 0, sizeMul: 1, shape: null, color: 0, active: false, resting: false, restThickness: 0, baseThickness: 0, restTargetY: 0, restAlpha: 1, bucket: -1 };
        this._particles.push(p);
        this.container.addChild(node);
        return p;
    }

    /** Retire a particle: mark dead, hide it, and return its record to the
     *  free stack for reuse. Does NOT remove it from `_live` — callers
     *  iterating `_live` swap-remove in place; one-off callers handle it. */
    _retire(p) {
        p.active = false;
        p.resting = false;
        p.node.visible = false;
        this._free.push(p);
    }

    _spawn() {
        const p = this._acquire();
        if (!p) return;
        const cfg = this.cfg;
        const confetti = cfg.engine === 'confetti';
        // Spawn point within the (optional) emission box.
        const ox = cfg.spawnW > 0 ? (Math.random() - 0.5) * cfg.spawnW : 0;
        const oy = cfg.spawnH > 0 ? (Math.random() - 0.5) * cfg.spawnH : 0;
        p.x = this._originX + ox;
        p.y = this._originY + oy;
        p.resting = false;     // reused pool nodes may have been parked
        // Emission cone: 0° = up, clockwise. spread is the full width.
        const half = (cfg.spread * DEG) / 2;
        const base = cfg.angle * DEG;
        const a = base + (Math.random() * 2 - 1) * half;
        const speed = lerp(cfg.speedMin, cfg.speedMax, Math.random());
        p.vx = Math.sin(a) * speed;
        p.vy = -Math.cos(a) * speed;
        p.age = 0;
        p.life = Math.max(0.05, cfg.lifetime);
        p.rot = Math.random() * Math.PI * 2;       // random start orientation
        // Randomise spin direction per particle so tumbling shapes don't
        // all rotate in lock-step — reads far more organic. Confetti
        // always tumbles, so it falls back to a lively default spin when
        // the designer leaves Spin at 0.
        const spinDeg = confetti && cfg.spin === 0 ? 220 : cfg.spin;
        p.spinRate = spinDeg * DEG * (Math.random() < 0.5 ? -1 : 1);
        // Fixed per-particle size multiplier (size jitter). Clamped so a
        // full ±100% spread can't collapse a particle to zero.
        p.sizeMul = cfg.sizeRandom > 0
            ? Math.max(0.1, 1 + (Math.random() * 2 - 1) * cfg.sizeRandom)
            : 1;
        p.active = true;
        this._live.push(p);     // joins the packed active list
        // Confetti = flat tumbling "paper" recoloured from the palette
        // (one fixed colour per piece). Normal = the configured shape,
        // recoloured each frame by the start→end tint lerp.
        p.color = confetti ? cfg.palette[(Math.random() * cfg.palette.length) | 0] : 0;
        const node = p.node;
        if (this._isImage()) {
            // Textured PNG sprite — no geometry to tessellate. Keep the
            // sprite's texture in sync (it may have loaded after spawn).
            if (this._texture && node.texture !== this._texture) node.texture = this._texture;
            p.shape = 'image';
        } else {
            const effShape = confetti ? 'confetti' : cfg.shape;
            // Geometry is tessellated ONCE (white) and reused; only rebuild
            // when this pooled node last drew a different shape.
            if (p.shape !== effShape) {
                buildShape(node, effShape);
                p.shape = effShape;
            }
        }
        node.visible = true;
        // Paper confetti is opaque (never glows); only the normal engine
        // honours additive blend (shapes + textured sprites alike).
        node.blendMode = (!confetti && cfg.blend === 'add') ? 'add' : 'normal';
    }

    /** Advance the simulation by `dt` seconds. */
    update(dt) {
        if (!(dt > 0)) return;
        const cfg = this.cfg;
        const ox = this._originX, oy = this._originY;
        const hasRadial = cfg.radialAccel !== 0;
        const hasTangential = cfg.tangentialAccel !== 0;
        const hasDamping = cfg.damping > 0;
        const hasTurbulence = cfg.turbulence !== 0;
        // Advance the turbulence phase clock and pre-compute its constants
        // so the per-particle loop stays a few cheap sin/cos calls.
        if (hasTurbulence) this._turbTime += dt * cfg.turbSpeed;
        const tf = cfg.turbScale;       // spatial frequency (1/px)
        const tph = this._turbTime;     // evolving phase

        // Collision is opt-in; when off (the default) we skip all of this
        // and integrate with a plain add — emitters that don't collide
        // pay nothing. When on, resolve each particle against the host's
        // tile grid (+ optional player body) once per frame. Accumulation
        // also needs ground detection, so it implicitly enables the path.
        const collide = (cfg.collide !== 'off' || cfg.accumulate) && !!this._colliders;
        const cs = collide ? this._colliders.cellSize : 0;
        const solidAt = collide ? this._colliders.solidAt : null;
        const oneWayAt = collide ? this._colliders.oneWayAt : null;
        const bounce = clamp(num(cfg.bounce, 0), 0, 1);
        // Snapshot dynamic bodies once per frame (not per particle). Needed
        // for the 'ground+player' hard bounce AND for player interaction
        // (wake/push/attract/repel/swirl), which works in any collide mode.
        const interactMode = cfg.playerInteract || 'off';
        const hasInteract = interactMode !== 'off' && cfg.interactStrength > 0;
        const wantBodies = this._bodyProvider && (cfg.collide === 'ground+player' || hasInteract);
        const bodies = wantBodies ? this._bodyProvider() : null;
        // Only the 'ground+player' mode does the hard point-vs-AABB push-out
        // inside the collide integrator; interaction-only emitters just steer.
        const integrateBodies = (collide && cfg.collide === 'ground+player') ? bodies : null;

        // Continuous emission via a fractional accumulator so non-integer
        // rates (e.g. 0.5/s) still emit smoothly. Gated by `_emitting`
        // so a proximity trigger can pause the stream (one-shot bursts
        // come through `burst()` instead).
        if (this._emitting) {
            this._accum += cfg.rate * dt;
            while (this._accum >= 1) {
                this._spawn();
                this._accum -= 1;
            }
        }

        // Hoist the per-frame-constant config reads used by EVERY active
        // particle into locals — saves a property lookup per field per
        // particle, which adds up on weak CPUs at high counts.
        const gravity = cfg.gravity;
        const sStart = cfg.startSize, sEnd = cfg.endSize;
        const aStart = cfg.startAlpha, aEnd = cfg.endAlpha;
        const cStart = cfg.startColor, cEnd = cfg.endColor;
        const radialA = cfg.radialAccel, tangentialA = cfg.tangentialAccel;
        const turb = cfg.turbulence, damp = cfg.damping;
        const interactK = cfg.interactStrength, interactR = cfg.interactRadius;
        const refDim = this._texRefDim;
        const interact = hasInteract && bodies;
        // Per-frame easing factor for resting flakes drifting to their pile
        // target (smooth settle / compaction). Clamped so a big dt can't
        // overshoot.
        const settleK = Math.min(1, ACCUM_SETTLE * dt);

        // Walk ONLY the active list, swap-removing on death so the hot loop
        // never touches dead pool slots. `i` is not advanced when we
        // swap-remove (the swapped-in record must be processed in place).
        const live = this._live;
        let i = 0;
        while (i < live.length) {
            const p = live[i];
            p.age += dt;
            if (p.age >= p.life) {
                // A resting flake dying lets the pile above it settle down.
                if (p.resting) onRestingDeath(this, p);
                this._retire(p);
                const last = live.pop();
                if (i < live.length) live[i] = last;
                continue;
            }

            // Parked (resting) flake — a real, settled particle in the pile.
            // Its size/tint/rotation were frozen at landing (`parkParticle`);
            // the only motion is a smooth EASE toward its column target when
            // the pile settles (a flake below died) or compacts (weight
            // presses it down). Once it has reached the target the branch is
            // a single compare and we write nothing — a deep pile (hundreds
            // of resting flakes) stays essentially free. The death fade runs
            // over the last slice of life.
            if (p.resting) {
                const ty = p.restTargetY;
                const ny = p.node.y;
                if (ny !== ty) {
                    const dy = ty - ny;
                    const next = (dy < 0.3 && dy > -0.3) ? ty : ny + dy * settleK;
                    p.node.y = next;
                    p.y = next;
                }
                const lt = p.age / p.life;
                if (lt > 0.85) p.node.alpha = p.restAlpha * Math.max(0, 1 - (lt - 0.85) / 0.15);
                i++;
                continue;
            }

            // Origin-relative forces → swirl ("vortex", tangential) and
            // blast/implode ("push/pulse", radial).
            if (hasRadial || hasTangential) {
                let dx = p.x - ox, dy = p.y - oy;
                let len = Math.sqrt(dx * dx + dy * dy);
                if (len < 1e-4) len = 1e-4;
                const rx = dx / len, ry = dy / len;   // radial unit
                if (hasRadial) {
                    p.vx += rx * radialA * dt;
                    p.vy += ry * radialA * dt;
                }
                if (hasTangential) {
                    // perpendicular (−ry, rx) = counter-clockwise tangent
                    p.vx += -ry * tangentialA * dt;
                    p.vy += rx * tangentialA * dt;
                }
            }

            // Turbulent displace: sample a cheap, divergence-light flow
            // field (layered sines in position + the phase clock) and add
            // it as acceleration. Each axis reads a field offset by the
            // OTHER coordinate so the result swirls rather than shears —
            // wind gusts, smoke eddies, snow drift. No noise tables, just
            // a handful of trig calls per particle.
            if (hasTurbulence) {
                const fx = Math.sin(p.y * tf + tph) + 0.5 * Math.sin(p.y * tf * 2.3 - tph * 1.3);
                const fy = Math.cos(p.x * tf - tph) + 0.5 * Math.cos(p.x * tf * 2.3 + tph * 0.7);
                p.vx += fx * turb * dt;
                p.vy += fy * turb * dt;
            }

            // Player interaction: a nearby body (the player) drags / pushes /
            // pulls / swirls particles depending on the chosen mode — snow
            // swept into a wake, leaves parted by a bow-wave, sparks pulled
            // into a magnet, embers orbiting the hero, etc.
            if (interact) applyPlayerInteract(p, dt, bodies, interactMode, interactK, interactR);

            // Gravity, then drag, then integrate.
            p.vy += gravity * dt;
            if (hasDamping) {
                const f = Math.max(0, 1 - damp * dt);
                p.vx *= f;
                p.vy *= f;
            }
            if (collide) {
                // 'rest' = parked into the pile (stays alive as a settled
                // flake — `parkParticle` already froze its look). 'dead' =
                // the column is full, so retire it. false = kept moving.
                const r = integrateCollide(this, p, dt, cs, solidAt, oneWayAt, bounce, integrateBodies);
                if (r === 'dead') {
                    this._retire(p);
                    const last = live.pop();
                    if (i < live.length) live[i] = last;
                    continue;
                }
                if (r === 'rest') { i++; continue; }   // visuals set in parkParticle
            } else {
                p.x += p.vx * dt;
                p.y += p.vy * dt;
            }
            p.rot += p.spinRate * dt;

            // Cheap per-frame visual update: transform + tint only.
            const t = p.age / p.life;
            const size = Math.max(0.5, lerp(sStart, sEnd, t) * p.sizeMul);
            const node = p.node;
            const isImage = p.shape === 'image';
            node.x = p.x;
            node.y = p.y;
            // Shapes are drawn at BASE; textured sprites scale off the
            // texture's longest side so `size` reads as on-screen pixels.
            node.scale.set(size / (isImage ? refDim : BASE));
            node.alpha = clamp(lerp(aStart, aEnd, t), 0, 1);
            // Textured sprites show their own colours (white tint);
            // confetti keeps its fixed palette colour; normal lerps start→end.
            node.tint = isImage ? 0xffffff
                : (p.shape === 'confetti' ? p.color : lerpColor(cStart, cEnd, t));
            // Sparks streak along their velocity; other shapes tumble.
            node.rotation = p.shape === 'spark' ? Math.atan2(p.vy, p.vx) : p.rot;
            i++;
        }
    }

    /** Drop every particle but keep the pool — used when the emitter
     *  origin teleports and you don't want a visible streak. */
    reset() {
        for (const p of this._particles) {
            p.active = false;
            p.resting = false;
            p.node.visible = false;
        }
        // Every record is now free; nothing is live.
        this._live.length = 0;
        this._free.length = 0;
        for (const p of this._particles) this._free.push(p);
        this._accum = 0;
        // Drop any accumulated buildup too (origin teleport / restart).
        if (this._stacks) this._stacks.clear();
    }

    destroy() {
        for (const p of this._particles) {
            if (p.node.parent) p.node.parent.removeChild(p.node);
            p.node.destroy?.();
        }
        this._particles.length = 0;
        this._live.length = 0;
        this._free.length = 0;
        this._stacks = null;
    }
}
